import { useState, useEffect } from 'react';
import api from '../../api/api';

export default function Lettrage() {
  const [thirdPartyType, setThirdPartyType] = useState('CLIENT');
  const [thirdParties, setThirdParties] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [proposals, setProposals] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const model = thirdPartyType === 'CLIENT' ? 'clients' : 'suppliers';
        const { data } = await api.get(`/${model}?limit=500`);
        setThirdParties(data?.clients || data?.suppliers || data?.items || []);
      } catch { setThirdParties([]); }
    };
    fetchParties();
  }, [thirdPartyType]);

  const loadProposals = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/accounting/matching/proposals', {
        params: { thirdPartyType, thirdPartyId: selectedId },
      });
      setProposals(data.items || []);
    } catch { setProposals([]); }
    setLoading(false);
  };

  const loadHistory = async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get('/accounting/matching/history', {
        params: { thirdPartyType, thirdPartyId: selectedId },
      });
      setHistory(data.items || []);
    } catch { setHistory([]); }
  };

  useEffect(() => { if (selectedId) { loadProposals(); loadHistory(); } }, [selectedId]);

  const toggleLine = (id) => {
    setSelectedLines(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const applyMatching = async () => {
    if (!selectedLines.length) return;
    try {
      const accountCode = thirdPartyType === 'CLIENT' ? '411100' : '401100';
      await api.post('/accounting/matching/apply', {
        accountCode, thirdPartyType, thirdPartyId: selectedId, lineIds: selectedLines,
      });
      setSelectedLines([]);
      loadProposals();
      loadHistory();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const totalSelected = proposals.filter(p => selectedLines.includes(p._id))
    .reduce((s, l) => s + Math.abs(l.solde), 0);

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-xl font-bold mb-4">Lettrage Comptable</h1>

      <div className="flex gap-4 mb-4 items-end">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Type</label>
          <select className="border rounded px-3 py-2 text-sm" value={thirdPartyType} onChange={e => { setThirdPartyType(e.target.value); setSelectedId(''); setProposals([]); setSelectedLines([]); }}>
            <option value="CLIENT">Clients</option>
            <option value="SUPPLIER">Fournisseurs</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs text-gray-600 mb-1">{thirdPartyType === 'CLIENT' ? 'Client' : 'Fournisseur'}</label>
          <select className="border rounded px-3 py-2 text-sm w-full" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            <option value="">-- Sélectionner --</option>
            {thirdParties.map(t => (
              <option key={t._id} value={t._id}>{t.name || t.companyName || t.company || t.nom || t._id}</option>
            ))}
          </select>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm" onClick={loadProposals}>Rafraîchir</button>
      </div>

      {loading && <p className="text-gray-500">Chargement...</p>}

      {proposals.length > 0 && (
        <div className="bg-white rounded shadow mb-4">
          <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
            <span className="font-semibold text-sm">Écritures à lettrer ({proposals.length})</span>
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600">Sélectionné: {totalSelected.toLocaleString('fr-TN', { minimumFractionDigits: 3 })} TND</span>
              <button className="bg-green-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50" disabled={!selectedLines.length} onClick={applyMatching}>
                Lettrer ({selectedLines.length})
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-100 text-xs uppercase text-gray-600">
                <th className="p-2 w-8"><input type="checkbox" onChange={e => { setSelectedLines(e.target.checked ? proposals.map(p => p._id) : []); }} checked={selectedLines.length === proposals.length && proposals.length > 0} /></th>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">N°</th>
                <th className="p-2 text-left">Compte</th>
                <th className="p-2 text-left">Libellé</th>
                <th className="p-2 text-right">Débit</th>
                <th className="p-2 text-right">Crédit</th>
                <th className="p-2 text-right">Solde</th>
              </tr></thead>
              <tbody>
                {proposals.map(p => (
                  <tr key={p._id} className={`border-t hover:bg-blue-50 ${selectedLines.includes(p._id) ? 'bg-blue-50' : ''}`}>
                    <td className="p-2 text-center"><input type="checkbox" checked={selectedLines.includes(p._id)} onChange={() => toggleLine(p._id)} /></td>
                    <td className="p-2">{new Date(p.entryDate).toLocaleDateString('fr-TN')}</td>
                    <td className="p-2">{p.entryNumber}</td>
                    <td className="p-2 font-mono">{p.accountCode}</td>
                    <td className="p-2">{p.label || p.sourceRef || '-'}</td>
                    <td className="p-2 text-right">{p.debit?.toLocaleString('fr-TN', { minimumFractionDigits: 3 })}</td>
                    <td className="p-2 text-right">{p.credit?.toLocaleString('fr-TN', { minimumFractionDigits: 3 })}</td>
                    <td className={`p-2 text-right font-semibold ${p.solde > 0 ? 'text-red-600' : 'text-green-600'}`}>{p.solde?.toLocaleString('fr-TN', { minimumFractionDigits: 3 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && proposals.length === 0 && selectedId && (
        <p className="text-gray-500 mb-4">Aucune écriture à lettrer pour ce {thirdPartyType === 'CLIENT' ? 'client' : 'fournisseur'}.</p>
      )}

      {history.length > 0 && (
        <div className="bg-white rounded shadow">
          <div className="p-3 border-b bg-gray-50 font-semibold text-sm">Historique des lettrages ({history.length})</div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-100 text-xs uppercase text-gray-600">
                <th className="p-2 text-left">Date</th><th className="p-2 text-left">Compte</th><th className="p-2 text-left">Statut</th><th className="p-2 text-right">Lignes</th>
              </tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h._id} className="border-t">
                    <td className="p-2">{new Date(h.createdAt).toLocaleDateString('fr-TN')}</td>
                    <td className="p-2 font-mono">{h.accountCode}</td>
                    <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${h.status === 'CLOSED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{h.status}</span></td>
                    <td className="p-2 text-right">{h.items?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
